import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, BarChart3, Loader2, RefreshCw, TrendingUp, Scale,
  Users, Truck,
} from "lucide-react";

const TABS = [
  { key: "pl", label: "Profit & Loss", icon: TrendingUp },
  { key: "bs", label: "Balance Sheet", icon: Scale },
  { key: "ar", label: "Aged Receivables", icon: Users },
  { key: "ap", label: "Aged Payables", icon: Truck },
];

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function XeroReportsPage() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState("pl");
  const [fromDate, setFromDate] = useState(startOfMonth());
  const [toDate, setToDate] = useState(today());
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/xero/tenants");
      setTenant((data || []).find((t) => t.tenant_id === tenantId)
        || { tenant_id: tenantId, tenant_name: tenantId });
    } catch (e) { logger.debug("XeroReports: tenant lookup skipped", e); }
  }, [tenantId]);
  useEffect(() => { loadTenant(); }, [loadTenant]);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      let url;
      if (tab === "pl") {
        url = `/integrations/xero/tenants/${tenantId}/reports/profit-and-loss?from_date=${fromDate}&to_date=${toDate}`;
      } else if (tab === "bs") {
        url = `/integrations/xero/tenants/${tenantId}/reports/balance-sheet?as_of=${asOf}`;
      } else if (tab === "ar") {
        url = `/integrations/xero/tenants/${tenantId}/reports/aged-receivables`;
      } else if (tab === "ap") {
        url = `/integrations/xero/tenants/${tenantId}/reports/aged-payables`;
      }
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  }, [tenantId, tab, fromDate, toDate, asOf]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl" data-testid="xero-reports-page">
      <Link to="/integrations/xero"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="xero-reports-back">
        <ArrowLeft className="h-3 w-3" /> Back to Xero
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <BarChart3 className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div className="flex-1">
          <div className="label-overline">XERO · FINANCIAL REPORTS</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {tenant?.tenant_name || "…"}
          </h1>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} data-testid="xero-reports-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* ── Tabs ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-4 h-10 rounded-sm border font-display font-bold text-sm inline-flex items-center gap-2 transition-colors ${
                active ? "bg-zinc-900 text-white border-zinc-900"
                       : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`xero-reports-tab-${t.key}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Filters (per tab) ── */}
      <Card className="rounded-sm border-zinc-200 mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          {tab === "pl" && (
            <>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                From
                <Input type="date" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-1 h-9 text-xs font-mono"
                  data-testid="xero-reports-pl-from" />
              </label>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                To
                <Input type="date" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="mt-1 h-9 text-xs font-mono"
                  data-testid="xero-reports-pl-to" />
              </label>
            </>
          )}
          {tab === "bs" && (
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              As of
              <Input type="date" value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                className="mt-1 h-9 text-xs font-mono"
                data-testid="xero-reports-bs-asof" />
            </label>
          )}
          {(tab === "ar" || tab === "ap") && (
            <div className="text-xs text-zinc-500">
              Computed from open <span className="font-mono font-semibold">
                {tab === "ar" ? "ACCREC" : "ACCPAY"}
              </span> invoices (AUTHORISED + SUBMITTED with amount due &gt; 0).
              <span className="block text-[10px] text-zinc-400 mt-0.5">
                Up to 500 invoices scanned per type — surface a warning if hit.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Report content ── */}
      {loading ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </CardContent>
        </Card>
      ) : data && (tab === "pl" || tab === "bs") ? (
        <XeroReportRender report={data} testid={`xero-reports-render-${tab}`} />
      ) : data && (tab === "ar" || tab === "ap") ? (
        <AgedReport data={data} kind={tab} testid={`xero-reports-aged-${tab}`} />
      ) : (
        <div className="text-center text-sm italic text-zinc-500 py-8">No data.</div>
      )}
    </div>
  );
}

// ── P&L / Balance Sheet renderer ─────────────────────────────────────
function XeroReportRender({ report, testid }) {
  const headers = report.headers || [];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={testid}>
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="font-display font-bold text-base">{report.title}</div>
          {report.report_titles && report.report_titles.slice(1).length > 0 && (
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {report.report_titles.slice(1).join(" · ")}
            </div>
          )}
        </div>
        {(report.sections || []).map((s, idx) => (
          <div key={`${s.title || "sec"}-${idx}`}
            className="border-b border-zinc-100 last:border-b-0">
            {s.title && (
              <div className="px-4 py-2 bg-zinc-50/60 font-display font-bold text-sm text-zinc-800 uppercase tracking-[0.05em]">
                {s.title}
              </div>
            )}
            <div>
              {(s.rows || []).map((r, ri) => (
                <ReportCellRow key={`${(r.cells || [])[0]?.value || "row"}-${ri}`}
                  cells={r.cells || []}
                  type={r.type} headers={headers} />
              ))}
              {s.summary && (
                <ReportCellRow cells={s.summary.cells || []}
                  type="SummaryRow" headers={headers} />
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ReportCellRow({ cells, type, headers }) {
  const isSummary = type === "SummaryRow";
  const numCols = Math.max(headers.length, cells.length, 2);
  return (
    <div
      className={`grid gap-2 px-4 py-1.5 text-sm border-b border-zinc-50 last:border-b-0 ${
        isSummary ? "bg-zinc-50 font-bold" : "hover:bg-zinc-50"
      }`}
      style={{ gridTemplateColumns: `1fr ${"110px ".repeat(numCols - 1).trim()}` }}>
      {cells.map((v, i) => (
        <span key={`${i}-${String(v?.value ?? v ?? "")}`}
          className={i === 0 ? "truncate" : "font-mono text-right"}>
          {v || (i === 0 ? "" : "")}
        </span>
      ))}
    </div>
  );
}

// ── Aged R/P renderer ────────────────────────────────────────────────
function AgedReport({ data, kind, testid }) {
  const totals = data.totals || {};
  const rows = data.rows || [];
  const isAR = kind === "ar";
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <SummaryPill label="Contacts" value={totals.contact_count ?? 0} />
        <SummaryPill label="Current" value={fmtMoney(totals.current)} />
        <SummaryPill label="1-30 days" value={fmtMoney(totals["1_30"])}
          accent={(totals["1_30"] ?? 0) > 0 ? "text-amber-700" : ""} />
        <SummaryPill label="31-60 days" value={fmtMoney(totals["31_60"])}
          accent={(totals["31_60"] ?? 0) > 0 ? "text-amber-700" : ""} />
        <SummaryPill label="61-90 days" value={fmtMoney(totals["61_90"])}
          accent={(totals["61_90"] ?? 0) > 0 ? "text-red-700" : ""} />
        <SummaryPill label="Over 90 days" value={fmtMoney(totals.over_90)}
          accent={(totals.over_90 ?? 0) > 0 ? "text-red-700" : ""} />
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0">
          <div className="grid grid-cols-[1fr_100px_100px_100px_100px_100px_120px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
            <span>{isAR ? "Customer" : "Supplier"}</span>
            <span className="text-right">Current</span>
            <span className="text-right">1-30 d</span>
            <span className="text-right">31-60 d</span>
            <span className="text-right">61-90 d</span>
            <span className="text-right">90+ d</span>
            <span className="text-right">Total</span>
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500">
              No open invoices.
            </div>
          ) : rows.map((r) => (
            <div key={r.contact_id}
              className="grid grid-cols-[1fr_100px_100px_100px_100px_100px_120px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
              data-testid={`xero-aged-row-${r.contact_id}`}>
              <span className="truncate font-semibold">{r.contact_name}</span>
              <CellAmt v={r.current} muted />
              <CellAmt v={r["1_30"]} amber />
              <CellAmt v={r["31_60"]} amber />
              <CellAmt v={r["61_90"]} red />
              <CellAmt v={r.over_90} red />
              <span className="font-mono text-right font-bold">
                {fmtMoney(r.total)}
              </span>
            </div>
          ))}
          {rows.length > 0 && (
            <div className="grid grid-cols-[1fr_100px_100px_100px_100px_100px_120px] gap-2 px-3 py-2 bg-zinc-50 text-sm items-center border-t border-zinc-200">
              <span className="font-display font-bold uppercase tracking-[0.08em] text-xs">
                Total
              </span>
              <span className="font-mono text-right">{fmtMoney(totals.current)}</span>
              <span className="font-mono text-right">{fmtMoney(totals["1_30"])}</span>
              <span className="font-mono text-right">{fmtMoney(totals["31_60"])}</span>
              <span className="font-mono text-right">{fmtMoney(totals["61_90"])}</span>
              <span className="font-mono text-right">{fmtMoney(totals.over_90)}</span>
              <span className="font-mono text-right font-bold">{fmtMoney(totals.total)}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CellAmt({ v, muted, amber, red }) {
  const n = Number(v || 0);
  const color = n === 0 ? "text-zinc-400"
    : red ? "text-red-700 font-semibold"
    : amber ? "text-amber-700"
    : muted ? "text-zinc-700"
    : "";
  return <span className={`font-mono text-right ${color}`}>{fmtMoney(v)}</span>;
}

function SummaryPill({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
